import { Box, IconButton, Modal, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

import RadiusButton from '../../../../components/RadiusButton';

const ACTION_LABELS = { added: 'Added', updated: 'Updated', existed: 'Already existed' };
const ACTION_COLORS = { added: 'success.main', updated: 'primary.main', existed: 'text.secondary' };
const TYPE_LABELS = { venue: 'Venue', country: 'Country', state: 'State', city: 'City' };

const savedColumns = [
    { id: 'row', label: 'Row', width: 64 },
    { id: 'action', label: 'Result', width: 130 },
    { id: 'type', label: 'Type', width: 80 },
    { id: 'name', label: 'Name' },
    { id: 'venueCode', label: 'Code', width: 110 },
    { id: 'maxCount', label: 'Max count', width: 100 },
    { id: 'location', label: 'Location' },
];

const failedColumns = [
    { id: 'row', label: 'Row', width: 64 },
    { id: 'message', label: 'Error' },
];

const renderSavedCell = (columnId, row) => {
    switch (columnId) {
        case 'action':
            return <Box component='span' sx={{ color: ACTION_COLORS[row.action], fontWeight: 600 }}>{ACTION_LABELS[row.action] ?? row.action}</Box>;
        case 'type':
            return TYPE_LABELS[row.type] ?? row.type;
        case 'venueCode':
            return row.venueCode ? <span className='venue-code-chip'>{row.venueCode}</span> : '—';
        case 'maxCount':
            return row.maxCount == null ? '—' : Number(row.maxCount).toLocaleString('en-IN');
        default:
            return row[columnId] || '—';
    }
};

// Same table look as the other admin upload results: blue border, blue headers.
const ResultTable = ({ columns, rows, renderCell }) => (
    <Paper sx={{ width: '100%', overflow: 'hidden', bgcolor: 'background.base', borderRadius: 1 }}>
        <TableContainer sx={{ maxHeight: 240, px: 1, pb: 1, border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
            <Table stickyHeader size='small'>
                <TableHead>
                    <TableRow>
                        {columns.map((column) => (
                            <TableCell key={column.id} sx={{ color: 'primary.main', width: column.width }}>{column.label}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => (
                        <TableRow key={`${row.row}-${index}`}>
                            {columns.map((column) => (
                                <TableCell key={column.id}>{renderCell(column.id, row)}</TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </Paper>
);

// Row-by-row outcome of a bulk upload: what was saved and why the other rows were rejected.
export const BulkUploadResultModal = ({ open, result, fileName, onClose }) => {
    const succeeded = result?.succeeded ?? [];
    const errors = result?.errors ?? [];

    return (
        <Modal open={open} hideBackdrop={true} className='backdrop' onClose={onClose}>
            <Box className='modal-container venue-modal' sx={{ maxWidth: 900 }}>
                <Box className='venue-modal-header'>
                    <Typography variant='h6' component='h6' sx={{ color: 'primary.main' }}>Upload result</Typography>
                    {fileName && <span className='venue-pill'>{fileName}</span>}
                    <IconButton size='small' sx={{ ml: 'auto' }} onClick={onClose} aria-label='Close'>✕</IconButton>
                </Box>

                <Box className='venue-modal-body'>
                    <Box className='upload-summary'>
                        <span className='upload-summary-item upload-summary-item--good'>Saved: {succeeded.length}</span>
                        <span className='upload-summary-item upload-summary-item--bad'>Failed: {errors.length}</span>
                    </Box>

                    <Typography sx={{ fontWeight: 600 }}>Saved rows</Typography>
                    {succeeded.length
                        ? <ResultTable columns={savedColumns} rows={succeeded} renderCell={renderSavedCell} />
                        : <Typography variant='body2' color='text.secondary'>No rows were saved.</Typography>}

                    <Typography sx={{ fontWeight: 600 }}>Failed rows</Typography>
                    {errors.length
                        ? <ResultTable columns={failedColumns} rows={errors} renderCell={(columnId, row) => row[columnId]} />
                        : <Typography variant='body2' color='text.secondary'>No rows failed.</Typography>}
                </Box>

                <Box className='venue-modal-footer'>
                    <RadiusButton variant='contained' onClick={onClose}>Close</RadiusButton>
                </Box>
            </Box>
        </Modal>
    );
}
